// src/stores/chatStore.ts - FIXED UNREAD BADGE VERSION

import { create } from 'zustand';
import type { ChatStore, ChatMessage, BotState } from '../types/chat';
import { supabase } from '../lib/supabase';

// --- ADDED TRANSFORMATION LOGIC ---
export interface MinimalMealPlanPreview {
  title: string;
  duration_days: number;
  total_meals: number;
  calories_target: number;
  timestamp: string;
}

function transformMessagesForUI(messages: ChatMessage[]): ChatMessage[] {
  const planPreviewIndices: number[] = [];
  messages.forEach((msg, index) => {
    if (msg.role === 'assistant' && msg.component?.type === 'meal_plan_preview') {
      planPreviewIndices.push(index);
    }
  });

  // Proceed even if there is only 1 preview, because it might be finalized.
  if (planPreviewIndices.length === 0) {
    return messages;
  }

  const lastPreviewIndex = planPreviewIndices[planPreviewIndices.length - 1];
  const transformedMessages = [...messages];

  planPreviewIndices.forEach((msgIndex) => {
    const originalMessage = transformedMessages[msgIndex];
    const fullProps = originalMessage.component?.props;

    if (!fullProps) return;

    // Minimize if it's NOT the last one, OR if it's explicitly marked as finalized
    if (msgIndex < lastPreviewIndex || fullProps.finalized === true) {
      const totalMealsCount = fullProps.days?.reduce(
        (sum: number, day: any) => sum + (day.meals?.length || 0),
        0
      ) || 0;

      const minimalData: MinimalMealPlanPreview = {
        title: fullProps.plan_title || 'Untitled Meal Plan',
        duration_days: fullProps.duration_days || 0,
        total_meals: totalMealsCount,
        calories_target: fullProps.calories_target || 0,
        timestamp: originalMessage.timestamp,
      };

      transformedMessages[msgIndex] = {
        ...originalMessage,
        component: {
          type: 'minimal_meal_plan_card',
          props: minimalData,
        },
      };
    }
  });

  return transformedMessages;
}

// Pre-load notification sound ONCE
let notificationSound: HTMLAudioElement | null = null;

const playNotification = () => {
  if (typeof window === 'undefined') return;

  if (!notificationSound) {
    notificationSound = new Audio('/sounds/notification.mp3');
    notificationSound.volume = 0.3;
  }

  notificationSound.play().catch(() => {
    // Ignore if sound fails
  });
};

export const useChatStore = create<ChatStore>((set, get) => ({
  // State
  messages: [],
  botState: 'INITIAL',
  collectedData: {},
  currentPreview: null,
  sessionId: null,
  isInitializingSession: false,
  isStreaming: false,
  streamingContent: '',
  error: null,
  unreadCount: 0,
  hasMoreMessages: false,
  isLoadingMore: false,

  // ✅ FIX: Load messages WITHOUT calculating unread count
  loadMessages: async (sessionId: string, limit = 50, offset = 0) => {
    const { data, error } = await supabase
      .from('chat_messages')
      .select('*')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) {
      console.error('Failed to load messages:', error);
      return;
    }

    const formatted: ChatMessage[] = (data ?? [])
      .reverse()
      .map((msg: any) => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: msg.created_at,
        read_at: msg.read_at,
        visible: msg.metadata?.visible ?? true,
        component: msg.metadata?.component,
        actions: msg.metadata?.actions,
        function_call: msg.metadata?.function_call,
        function_name: msg.metadata?.function_name,
      }));

    if (offset > 0) {
      // Append older messages to the beginning
      set((state) => ({
        messages: transformMessagesForUI([...formatted, ...state.messages]),
        hasMoreMessages: data.length === limit,
        isLoadingMore: false
      }));
    } else {
      // Initial load - don't calculate unread here
      set({
        messages: transformMessagesForUI(formatted),
        hasMoreMessages: data.length === limit,
        isLoadingMore: false
      });

      // ✅ Fetch unread count separately to avoid race condition
      await get().fetchUnreadCount();
    }
  },

  loadMoreMessages: async () => {
    const { sessionId, messages, hasMoreMessages, isLoadingMore } = get();

    if (!sessionId || !hasMoreMessages || isLoadingMore) return;

    set({ isLoadingMore: true });
    await get().loadMessages(sessionId, 50, messages.length);
  },

  // Fetch accurate unread count from DB
  fetchUnreadCount: async () => {
    const { sessionId } = get();
    if (!sessionId) return;

    const { count, error } = await supabase
      .from('chat_messages')
      .select('*', { count: 'exact', head: true })
      .eq('session_id', sessionId)
      .eq('role', 'assistant')
      .is('read_at', null);

    if (!error && count !== null) {
      set({ unreadCount: count });
    }
  },

  // ✅ FIX: Update DB FIRST, then update state
  markSessionRead: async () => {
    const { sessionId, unreadCount } = get();
    if (!sessionId || unreadCount === 0) return;

    // Update database FIRST (wait for completion)
    const { error } = await supabase
      .from('chat_messages')
      .update({ read_at: new Date().toISOString() })
      .eq('session_id', sessionId)
      .is('read_at', null)
      .eq('role', 'assistant');

    if (error) {
      console.error('Failed to mark messages as read:', error);
      return;
    }

    // THEN update local state (after DB confirms)
    set((state) => ({
      unreadCount: 0,
      messages: state.messages.map((m) =>
        !m.read_at ? { ...m, read_at: new Date().toISOString() } : m
      )
    }));
  },

  incrementUnread: () => {
    set((state) => ({
      unreadCount: state.unreadCount + 1
    }));

    playNotification();
  },

  initSession: async () => {
    const { isInitializingSession, sessionId } = get();

    if (isInitializingSession || sessionId) {
      return sessionId;
    }

    set({ isInitializingSession: true });

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data: existing } = await supabase
        .from('chat_sessions')
        .select('id')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      let id: string;

      if (existing?.id) {
        id = existing.id;
      } else {
        const { data, error } = await supabase
          .from('chat_sessions')
          .insert({
            user_id: user.id,
            title: 'New Conversation',
            is_active: true,
          })
          .select('id')
          .single();

        if (error || !data) throw error;
        id = data.id;
      }

      set({ sessionId: id, error: null });

      // Load messages (which will also fetch unread count)
      await get().loadMessages(id);

      return id;
    } catch (err) {
      console.error('initSession failed:', err);
      set({ error: 'Failed to initialize chat session' });
      return null;
    } finally {
      set({ isInitializingSession: false });
    }
  },

  addMessage: async (message: ChatMessage) => {
    const { sessionId } = get();

    if (!sessionId) {
      console.error('No active session');
      return;
    }

    set((state) => {
      const exists = state.messages.some((m) => m.id === message.id);
      let updatedMessages;
      if (exists) {
        updatedMessages = state.messages.map((m) =>
          m.id === message.id ? message : m
        );
      } else {
        updatedMessages = [...state.messages, message];
      }
      return { messages: transformMessagesForUI(updatedMessages) };
    });

    const { error } = await supabase.from('chat_messages').upsert({
      id: message.id,
      session_id: sessionId,
      role: message.role,
      content: message.content,
      metadata: {
        visible: message.visible ?? true,
        component: message.component,
        actions: message.actions,
        function_call: message.function_call,
        function_name: message.function_name,
      },
      updated_at: new Date().toISOString(),
    });

    if (error) {
      console.error('Failed to save message:', error);
      set({ error: 'Failed to save message' });
    }
  },

  updateStreamingContent: (content: string) =>
    set({ streamingContent: content }),

  setIsStreaming: (streaming: boolean) =>
    set({ isStreaming: streaming }),

  setBotState: (botState: BotState) => set({ botState }),

  setCollectedData: (data: any) =>
    set((state) => ({
      collectedData: { ...state.collectedData, ...data },
    })),

  setCurrentPreview: (preview: any) =>
    set({ currentPreview: preview }),

  setSessionId: (id: string | null) => set({ sessionId: id }),

  setError: (error: string | null) => set({ error }),

  clearChat: async () => {
    const { sessionId } = get();
    if (!sessionId) return;

    await supabase
      .from('chat_messages')
      .delete()
      .eq('session_id', sessionId);

    set({
      messages: [],
      streamingContent: '',
      error: null,
      unreadCount: 0,
    });
  },

  resetConversation: async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) throw new Error('Not authenticated');

      const { sessionId } = get();

      if (sessionId) {
        await supabase
          .from('chat_sessions')
          .update({ is_active: false })
          .eq('id', sessionId);
      }

      const { data, error } = await supabase
        .from('chat_sessions')
        .insert({
          user_id: user.id,
          title: 'New Conversation',
          is_active: true,
        })
        .select('id')
        .single();

      if (error || !data) throw error;

      set({
        messages: [],
        botState: 'INITIAL',
        collectedData: {},
        currentPreview: null,
        sessionId: data.id,
        isStreaming: false,
        streamingContent: '',
        error: null,
        hasMoreMessages: false,
        isLoadingMore: false,
        unreadCount: 0,
      });

      return data.id;
    } catch (err) {
      console.error('resetConversation failed:', err);
      set({ error: 'Failed to reset conversation' });
      return null;
    }
  },

  resetStore: () => set({
    messages: [],
    botState: 'INITIAL',
    collectedData: {},
    currentPreview: null,
    sessionId: null,
    isInitializingSession: false,
    isStreaming: false,
    streamingContent: '',
    error: null,
    unreadCount: 0,
  })
}));